// See _README/ui-helpers.readme.md

/**
 * Bootstrap EE — Guidance Prompt
 *
 * Shows advice before an action runs, in a Bootstrap 5 modal shaped like the
 * standard confirm (title · body · Cancel / Continue). Each advice item is an
 * alert; a "suggest" item also carries a Yes/No choice with the best practice
 * preselected. Guidance, not enforcement: every item can be overridden.
 *
 * USAGE
 * -----
 *   const choices = await bsee.guide(advice, { title: 'Add New Look' });
 *   if (choices === null) { return; }          // Cancel / closed
 *   // choices = { close_nlr: '1' }             // one entry per choice, '1' = Yes, '0' = No
 *
 * ADVICE ITEM
 * -----------
 *   { rule, strength: 'suggest' | 'recommend' | 'warn', message,
 *     choice: null | { input, default, yes, no } }
 *
 * Resolves null on Cancel, the × button, Escape or a backdrop click.
 */

import { Modal } from 'bootstrap';

export interface GuideChoice {
    input: string;
    default: boolean;
    yes: string;
    no: string;
}

export interface GuideAdvice {
    rule: string;
    strength: 'suggest' | 'recommend' | 'warn';
    message: string;
    choice: GuideChoice | null;
}

export interface GuideOptions {
    title?: string;
    continueLabel?: string;
    /** Test seam: how the modal is shown and hidden. Defaults to Bootstrap's Modal. */
    modal?: (el: HTMLElement) => { show(): void; hide(): void };
}

export type GuideChoices = Record<string, '1' | '0'>;

const ALERT_VARIANTS: Record<GuideAdvice['strength'], string> = {
    suggest:   'info',
    recommend: 'info',
    warn:      'warning',
};

let promptCount = 0;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag);
    node.className = className;
    if (text !== undefined) {
        node.textContent = text;
    }
    return node;
}

function radio(name: string, value: '1' | '0', label: string, checked: boolean): HTMLElement {
    const wrap  = el('div', 'form-check form-check-inline');
    const input = el('input', 'form-check-input');
    input.type    = 'radio';
    input.name    = name;
    input.value   = value;
    input.id      = `${name}-${value}`;
    input.checked = checked;
    const text = el('label', 'form-check-label', label);
    text.htmlFor = input.id;
    wrap.append(input, text);
    return wrap;
}

function adviceAlert(item: GuideAdvice, promptId: string): HTMLElement {
    const alert = el('div', `alert alert-${ALERT_VARIANTS[item.strength]} mb-2`);
    alert.dataset.rule = item.rule;

    // BSEE alerts draw their own icon (::before, _alerts.scss) — the message is just text.
    alert.append(el('span', '', item.message));

    if (item.choice !== null) {
        const name    = `${promptId}-${item.choice.input}`;
        const choices = el('div', 'mt-2 js-guide-choice');
        choices.dataset.input = item.choice.input;
        choices.append(
            radio(name, '1', item.choice.yes, item.choice.default),
            radio(name, '0', item.choice.no, !item.choice.default),
        );
        alert.append(choices);
    }
    return alert;
}

/** Build the modal element (not yet attached or shown). Exported for tests. */
export function buildGuide(advice: GuideAdvice[], options: GuideOptions = {}): HTMLElement {
    promptCount += 1;
    const promptId = `bsee-guide-${promptCount}`;
    const warns    = advice.some(item => item.strength === 'warn');

    const modal = el('div', 'modal fade');
    modal.id       = promptId;
    modal.tabIndex = -1;
    modal.setAttribute('aria-hidden', 'true');

    const dialog  = el('div', 'modal-dialog');
    const content = el('div', 'modal-content');

    const header = el('div', 'modal-header');
    const close  = el('button', 'btn-close');
    close.type = 'button';
    close.setAttribute('data-bs-dismiss', 'modal');
    close.setAttribute('aria-label', 'Close');
    header.append(el('h5', 'modal-title', options.title ?? 'Before you continue'), close);

    const body = el('div', 'modal-body');
    advice.forEach(item => body.append(adviceAlert(item, promptId)));

    const footer = el('div', 'modal-footer');
    const cancel = el('button', 'btn btn-secondary js-guide-cancel', 'Cancel');
    cancel.type = 'button';
    cancel.setAttribute('data-bs-dismiss', 'modal');
    const go = el('button', `btn ${warns ? 'btn-warning' : 'btn-primary'} js-guide-continue`, options.continueLabel ?? 'Continue');
    go.type = 'button';
    footer.append(cancel, go);

    content.append(header, body, footer);
    dialog.append(content);
    modal.append(dialog);
    return modal;
}

/** The selected answer for every choice in a built prompt. Exported for tests. */
export function readChoices(modal: HTMLElement): GuideChoices {
    const choices: GuideChoices = {};
    modal.querySelectorAll<HTMLElement>('.js-guide-choice').forEach(group => {
        const picked = group.querySelector<HTMLInputElement>('input:checked');
        choices[group.dataset.input as string] = picked !== null && picked.value === '1' ? '1' : '0';
    });
    return choices;
}

function bootstrapModal(modalEl: HTMLElement): { show(): void; hide(): void } {
    return Modal.getOrCreateInstance(modalEl);
}

/** Show the advice; resolves with the choices on Continue, null on Cancel / close. */
export function guide(advice: GuideAdvice[], options: GuideOptions = {}): Promise<GuideChoices | null> {
    const modalEl = buildGuide(advice, options);
    document.body.append(modalEl);
    const modal = (options.modal ?? bootstrapModal)(modalEl);

    return new Promise(resolve => {
        let result: GuideChoices | null = null;

        modalEl.querySelector('.js-guide-continue')!.addEventListener('click', () => {
            result = readChoices(modalEl);
            modal.hide();
        });

        // Every way out (Continue, Cancel, ×, Escape, backdrop) ends in hidden.bs.modal.
        modalEl.addEventListener('hidden.bs.modal', () => {
            modalEl.remove();
            resolve(result);
        });

        modal.show();
    });
}
